import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Stack, Alert
} from '@mui/material';
import { friendsApi, friendGroupsApi } from '../services/api';
import LoadingButton from './LoadingButton';
import SplitParticipantsFields from './split/SplitParticipantsFields';
import { getParticipants, buildSplits } from '../utils/splitMath';
import { formatMoney } from '../utils/money';

const emptyForm = () => ({
  splitType: 'group',
  groupId: '',
  selectedFriends: [],
  splitMethod: 'equal',
  customSplits: {}
});

// Split one of your own bank transactions; you are always the payer
function SplitTransactionDialog({ open, transaction, onClose, onSuccess }) {
  const [friends, setFriends] = useState([]);
  const [groups, setGroups] = useState([]);
  const [me, setMe] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData(emptyForm());
      setError('');
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    try {
      const [friendsRes, groupsRes] = await Promise.all([
        friendsApi.getAll(),
        friendGroupsApi.getAll()
      ]);
      setFriends(friendsRes.data.friends || []);
      setMe(friendsRes.data.me || null);
      setGroups(groupsRes.data.groups || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load friends and groups');
    }
  };

  const amount = transaction ? Math.abs(parseFloat(transaction.amount)) : 0;
  const participants = getParticipants(formData, groups, me);

  const handleSubmit = async () => {
    if (!transaction) return;
    setError('');
    if (!me) {
      setError('Could not load your account details. Close and try again.');
      return;
    }
    if (formData.splitType === 'group' && !formData.groupId) {
      setError('Please select a group');
      return;
    }

    const result = buildSplits({
      amount,
      participants,
      splitMethod: formData.splitMethod,
      customSplits: formData.customSplits
    });
    if (result.error) {
      setError(result.error);
      return;
    }

    try {
      setSubmitting(true);
      await onSuccess({
        transactionId: transaction.id,
        groupId: formData.splitType === 'group' ? formData.groupId : null,
        splits: result.splits
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to split transaction');
    } finally {
      setSubmitting(false);
    }
  };

  if (!transaction) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Split Transaction with Friends</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Alert severity="info">
            Splitting: <strong>{transaction.description}</strong> - {formatMoney(amount)} (paid by you)
          </Alert>

          <SplitParticipantsFields
            formData={formData}
            onChange={setFormData}
            friends={friends}
            groups={groups}
            participants={participants}
            amount={amount}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          loading={submitting}
          disabled={participants.length < 2}
        >
          Split Transaction
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default SplitTransactionDialog;
